import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/config/reactQuery';
import { QUERY_KEYS } from '@/api/queryKeys';
import { ReleaseNoteApp, SaveReleaseNote } from '@/types';
import { releaseNotes } from '../ReleaseNotes';

type OptionsType = { enabled?: boolean };

const invalidateReleaseNotes = () => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.releaseNoteHistory] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.latestReleaseNote] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.releaseNotes] });
};

export const useLatestReleaseNote = (options?: OptionsType) =>
  useQuery({
    queryKey: [QUERY_KEYS.latestReleaseNote],
    queryFn: async () => (await releaseNotes.getLatest()).data,
    enabled: options?.enabled,
    retry: false,
  });

export const useDismissReleaseNote = () =>
  useMutation({
    mutationFn: (versionLabel: string) => releaseNotes.dismiss(versionLabel),
  });

export const useReleaseNoteHistoryPage = (
  take: number,
  options?: OptionsType,
) =>
  useQuery({
    queryKey: [QUERY_KEYS.releaseNoteHistory, take],
    queryFn: async () => (await releaseNotes.getHistory(0, take)).data,
    enabled: options?.enabled,
    placeholderData: keepPreviousData,
    retry: false,
  });

export const useReleaseNotes = (app?: ReleaseNoteApp, options?: OptionsType) =>
  useQuery({
    queryKey: [QUERY_KEYS.releaseNotes, app],
    queryFn: async () => (await releaseNotes.getAll(app)).data,
    enabled: options?.enabled,
    retry: false,
  });

export const useCreateReleaseNote = () =>
  useMutation({
    mutationFn: (data: SaveReleaseNote) => releaseNotes.create(data),
    onSuccess: (res) => {
      if (res.success) invalidateReleaseNotes();
    },
  });

export const useUpdateReleaseNote = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: number; data: SaveReleaseNote }) =>
      releaseNotes.update(id, data),
    onSuccess: (res) => {
      if (res.success) invalidateReleaseNotes();
    },
  });

export const useDeleteReleaseNote = () =>
  useMutation({
    mutationFn: (id: number) => releaseNotes.deleteById(id),
    onSuccess: (res) => {
      if (res.success) invalidateReleaseNotes();
    },
  });
